import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import {
    DischargeDestination,
    DischargeFollowUp,
    DischargeInfoGiven,
    DischargeStatus,
    SafeguardingECDS,
} from '../../../../controls/agyle/ecds';
import moment from 'moment';
import config from '../../../../app/config';
import _ from 'lodash';
import {ActivityTreatmentECDS} from '../../../../controls/agyle/dictionaries';
import {UtilsMomentJS} from '../../../../store/utilsMoment';
import {useStylesECDS} from './ecdsStyle';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import {useSelector} from 'react-redux';
import {
    activityTreatment,
    dischargeDestination,
    dischargeFollowUp,
    dischargeInfoGiven,
    dischargeStatus,
    safeguarding,
} from '../../../../store/reducers/dicts';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import {FormLabel} from '@mui/material';
import AgyleCheckBox from '../../../../components/ui/checkbox/agyleCheckBox';
import {getSafeguardingValues} from '../formsUtil';

const DischargeECDS = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();
    const {version} = props;

    // Discharge Dataset
    const [discharge, setDischarge] = useState({
        DecidedToAdmitDateAndTime: '',
        ActivityTreatmentFunctionCode_DecisionToAdmit: null,
        EmergencyCareDischargeStatus_SnomedCt: null,
        UrgentAndEmergencyCareActivityEndTimestamp: '',
        SafeguardingConcern_SnomedCt: [],
        EmergencyCareDischargeDestination_SnomedCt: null,
        OrganisationSiteIdentifier_DischargeFromEmergencyCare: '',
        EmergencyCareDischargeFollowUp_SnomedCt: null,
        EmergencyCareDischargeInformationGiven_SnomedCt: null,
        EmergencyCareClinicallyReadyToProceedTimeStamp: '',
        UrgentAndEmergencyCareDischargeInformationGivenIndicator: '',
    });
    const [pickers, setPickers] = useState({
        UrgentAndEmergencyCareActivityEndTimestamp: null,
        DecidedToAdmitDateAndTime: null,
        EmergencyCareClinicallyReadyToProceedTimeStamp: null,
    });

    // Validation
    const validationEnabled = config?.modules?.forms?.ecds?.validation || false;
    const [error, setError] = useState({
        admitDateAndTime: false,
        activity: false,
        dischargeStatus: false,
        activityEndTimestamp: false,
        orgId: false,
        safeguarding: false,
        dischargeDestination: false,
        dischargeFollowUp: false,
        dischargeInfoGiven: false,
        timestamp: false,
    });

    // For ReadOnly Data
    const disableData = useRef({});

    // invalid pickers
    const errorPicker = useRef({
        admitDateAndTime: null,
        activityEndTimestamp: null,
        proceedDate: null,
    });

    // Dictionaries
    const safeguardingDict = useSelector(safeguarding);
    const activityTreatmentDict = useSelector(activityTreatment);
    const dischargeDestinationDict = useSelector(dischargeDestination);
    const dischargeFollowUpDict = useSelector(dischargeFollowUp);
    const dischargeInfoGivenDict = useSelector(dischargeInfoGiven);
    const dischargeStatusDict = useSelector(dischargeStatus);

    // Initial Data
    useEffect(async () => {
        let picker = {};
        let data = {};
        let dataToBlock = {};

        const getObjects = (safeguarding) => {
            let arr = [];
            _.forEach(safeguarding, element => {
                arr.push(_.find(safeguardingDict, {ecdsVersion: version, snomedCode: element}));
            });
            return arr;
        };

        const checkLastObjectForDecidedToAdmit = (array, timeFiled, dateField) => {
            if (array && !_.isEmpty(array)) {
                const formatDate = array.map((item) => ({
                    ...item,
                    datetimeFormatted: UtilsMomentJS.getLocalDateFromUTCECDSFormat(item[dateField], item[timeFiled]),
                }));
                const sortArray = _.sortBy(formatDate, 'datetimeFormatted');
                const firstObject = sortArray[0];
                if ((firstObject?.datetimeFormatted && !dataToBlock.DecidedToAdmitTime) || (dataToBlock.DecidedToAdmitTime && moment(dataToBlock.DecidedToAdmitTime).isAfter(firstObject.datetimeFormatted))) {
                    dataToBlock = {
                        ...dataToBlock,
                        DecidedToAdmitDate: firstObject.datetimeFormatted,
                        DecidedToAdmitTime: firstObject.datetimeFormatted,
                        DecidedToAdmitDateAndTime: firstObject.datetimeFormatted,
                    };

                    picker = {
                        ...picker,
                        DecidedToAdmitDate: firstObject.datetimeFormatted,
                        DecidedToAdmitTime: firstObject.datetimeFormatted,
                        DecidedToAdmitDateAndTime: firstObject.datetimeFormatted,
                    };
                }
            }
        };

        if (props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.DischargeFromEmergencyCare) {
            const clone = _.cloneDeep(props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.DischargeFromEmergencyCare);
            const localAdmit = UtilsMomentJS.getLocalDateFromUTCECDSFormat(clone.DecidedToAdmitDate, clone.DecidedToAdmitTime);
            const localDeparture = UtilsMomentJS.getLocalDateFromUTCECDSFormat(clone.EmergencyCareDepartureDate, clone.EmergencyCareDepartureTime);
            const localRdyToProc = moment.utc(clone.EmergencyCareClinicallyReadyToProceedTimeStamp).local();

            dataToBlock = {
                ...data,
                ActivityTreatmentFunctionCode_DecisionToAdmit: clone.ActivityTreatmentFunctionCode_DecisionToAdmit
                    ? _.find(activityTreatmentDict, {__code: clone.ActivityTreatmentFunctionCode_DecisionToAdmit}) : null,
                DecidedToAdmitDate: clone.DecidedToAdmitDate ? moment(clone.DecidedToAdmitDate) : null,
                DecidedToAdmitTime: clone.DecidedToAdmitTime ? moment(clone.DecidedToAdmitTime, config.ecdsTimeFormat) : null,
                DecidedToAdmitDateAndTime: clone.DecidedToAdmitDateAndTime ? moment(clone.DecidedToAdmitDateAndTime) : null,
                EmergencyCareClinicallyReadyToProceedTimeStamp: clone.EmergencyCareClinicallyReadyToProceedTimeStamp
                    ? moment(clone.EmergencyCareClinicallyReadyToProceedTimeStamp) : null,
                EmergencyCareDepartureDate: clone.EmergencyCareDepartureDate ? moment(clone.EmergencyCareDepartureDate) : null,
                EmergencyCareDepartureTime: clone.EmergencyCareDepartureTime ? moment(clone.EmergencyCareDepartureTime, config.ecdsTimeFormat) : null,
                UrgentAndEmergencyCareActivityEndTimestamp: clone.UrgentAndEmergencyCareActivityEndTimestamp ? moment(clone.UrgentAndEmergencyCareActivityEndTimestamp) : null,
                EmergencyCareDischargeDestination_SnomedCt: clone.EmergencyCareDischargeDestination_SnomedCt
                    ? _.find(dischargeDestinationDict, {ecdsVersion: version, snomedCode: clone.EmergencyCareDischargeDestination_SnomedCt}) : null,
                EmergencyCareDischargeFollowUp_SnomedCt: clone.EmergencyCareDischargeFollowUp_SnomedCt
                    ? _.find(dischargeFollowUpDict, {ecdsVersion: version, snomedCode: clone.EmergencyCareDischargeFollowUp_SnomedCt}) : null,
                EmergencyCareDischargeInformationGiven_SnomedCt: clone.EmergencyCareDischargeInformationGiven_SnomedCt
                    ? _.find(dischargeInfoGivenDict, {ecdsVersion: version, snomedCode: clone.EmergencyCareDischargeInformationGiven_SnomedCt}) : null,
                EmergencyCareDischargeStatus_SnomedCt: clone.EmergencyCareDischargeStatus_SnomedCt
                    ? _.find(dischargeStatusDict, {ecdsVersion: version, snomedCode: clone.EmergencyCareDischargeStatus_SnomedCt}) : null,
                OrganisationSiteIdentifier_DischargeFromEmergencyCare: clone.OrganisationSiteIdentifier_DischargeFromEmergencyCare
                    ? clone.OrganisationSiteIdentifier_DischargeFromEmergencyCare : null,
                SafeguardingConcern_SnomedCt: clone.SafeguardingConcern_SnomedCt
                    ? getObjects(clone.SafeguardingConcern_SnomedCt) : [],
            };

            picker = {
                DecidedToAdmitDate: (clone.DecidedToAdmitDate && localAdmit) ? localAdmit : null,
                DecidedToAdmitTime: (clone.DecidedToAdmitTime && localAdmit) ? localAdmit : null,
                DecidedToAdmitDateAndTime: clone.DecidedToAdmitDateAndTime ? moment(clone.DecidedToAdmitDateAndTime) : null,
                EmergencyCareDepartureDate: (clone.EmergencyCareDepartureDate && localDeparture) ? localDeparture : null,
                EmergencyCareDepartureTime: (clone.EmergencyCareDepartureTime && localDeparture) ? localDeparture : null,
                UrgentAndEmergencyCareActivityEndTimestamp: clone.UrgentAndEmergencyCareActivityEndTimestamp ? moment(clone.UrgentAndEmergencyCareActivityEndTimestamp) : null,
                EmergencyCareClinicallyReadyToProceedTimeStamp: localRdyToProc || null,
            };
        }

        if (props?.patient?.metadata?.dischargeTimestampData) {
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareDepartureDate: moment(props?.patient?.metadata?.dischargeTimestampData?.departureDate),
                EmergencyCareDepartureTime: moment(props?.patient?.metadata?.dischargeTimestampData?.departureTime),
                UrgentAndEmergencyCareActivityEndTimestamp: moment(props?.patient?.metadata?.dischargeTimestampData?.departureTime),
                EmergencyCareDischargeStatus_SnomedCt: props?.patient?.metadata?.dischargeTimestampData?.dischargeStatus,
                EmergencyCareDischargeDestination_SnomedCt: props?.patient?.metadata?.dischargeTimestampData?.dischargeDestination,
                EmergencyCareDischargeFollowUp_SnomedCt: props?.patient?.metadata?.dischargeTimestampData?.dischargeFollowUp,
            };

            picker = {
                ...picker,
                EmergencyCareDepartureDate: moment(props?.patient?.metadata?.dischargeTimestampData?.departureDate),
                EmergencyCareDepartureTime: moment(props?.patient?.metadata?.dischargeTimestampData?.departureTime),
                UrgentAndEmergencyCareActivityEndTimestamp: moment(props?.patient?.metadata?.dischargeTimestampData?.departureTime),
            };
        }

        if (props.patient?.studies?.discharge) {
            const dischargeForm = props.patient?.studies?.discharge;
            const localDeparture = UtilsMomentJS.getLocalDateFromUTCECDSFormat(dischargeForm?.departureDate, dischargeForm?.departureTime);
            dataToBlock = {
                EmergencyCareDepartureDate: moment(dischargeForm.departureDate),
                EmergencyCareDepartureTime: moment(dischargeForm.departureTime).isValid()
                    ? moment(dischargeForm.departureTime) : moment(dischargeForm.departureTime, config.ecdsTimeFormat),
                UrgentAndEmergencyCareActivityEndTimestamp: dischargeForm?.departureDateAndTime ? moment(dischargeForm.departureDateAndTime) : localDeparture,
                EmergencyCareDischargeStatus_SnomedCt: dischargeForm.dischargeStatus,
                EmergencyCareDischargeDestination_SnomedCt: dischargeForm.dischargeDestination,
                EmergencyCareDischargeFollowUp_SnomedCt: dischargeForm.dischargeFollowUp,
            };

            picker = {
                ...picker,
                EmergencyCareDepartureDate: localDeparture || null,
                EmergencyCareDepartureTime: localDeparture || null,
                UrgentAndEmergencyCareActivityEndTimestamp: dischargeForm?.departureDateAndTime ? moment(dischargeForm.departureDateAndTime) : localDeparture,
            };

            dataToBlock = {
                ...dataToBlock,
                EmergencyCareDepartureDate: dischargeForm?.amendDepartureDate
                    ? moment(dischargeForm?.amendDepartureDate)
                    : dataToBlock.EmergencyCareDepartureDate,
                EmergencyCareDepartureTime: dischargeForm?.amendDepartureTime
                    ? moment(dischargeForm.amendDepartureTime, config.defaultTimeFormat)
                    : dataToBlock.EmergencyCareDepartureTime,
            };
            dataToBlock = {
                ...dataToBlock,
                OrganisationSiteIdentifier_DischargeFromEmergencyCare: dischargeForm?.hospital
                    ? dischargeForm?.hospital.__id
                    : dataToBlock.OrganisationSiteIdentifier_DischargeFromEmergencyCare,
            };
        }

        dataToBlock = {
            ...dataToBlock,
            SafeguardingConcern_SnomedCt: getSafeguardingValues(props.patient),
        };

        if (props.patient.csmContactedTime) {
            const csm = props.patient.csmContactedTime;
            const localAdmit = moment(csm?.when);

            dataToBlock = {
                ...dataToBlock,
                DecidedToAdmitDate: csm?.when
                    ? (moment(csm?.when, config.defaultDateTimeFormat).isValid() ? moment(csm?.when, config.defaultDateTimeFormat) : moment(csm?.when))
                    : null,
                DecidedToAdmitTime: csm?.when
                    ? (moment(csm?.when, config.defaultDateTimeFormat).isValid() ? moment(csm?.when, config.defaultDateTimeFormat) : moment(csm?.when))
                    : null,
                DecidedToAdmitDateAndTime: csm?.when
                    ? (moment(csm?.when, config.defaultDateTimeFormat).isValid() ? moment(csm?.when, config.defaultDateTimeFormat) : moment(csm?.when))
                    : null,
                ActivityTreatmentFunctionCode_DecisionToAdmit: csm?.activityTreatment || null,
            };

            picker = {
                ...picker,
                DecidedToAdmitDate: localAdmit || null,
                DecidedToAdmitTime: localAdmit || null,
                DecidedToAdmitDateAndTime: localAdmit || null,
            };
        }

        if (props.patient?.studies?.bedRequest) {
            const localAdmit = moment(props.patient.studies?.bedRequest?.requestedWhen);

            dataToBlock = {
                ...dataToBlock,
                DecidedToAdmitDate: props.patient.studies?.bedRequest?.when ? moment(props.patient.studies?.bedRequest?.when) : null,
                DecidedToAdmitTime: props.patient.studies?.bedRequest?.when ? moment(props.patient.studies?.bedRequest?.when) : null,
                DecidedToAdmitDateAndTime: props.patient.studies?.bedRequest?.when ? moment(props.patient.studies?.bedRequest?.when) : null,
                ActivityTreatmentFunctionCode_DecisionToAdmit: props.patient?.studies?.bedRequest?.activityTreatment
                    ? props.patient?.studies?.bedRequest?.activityTreatment : null,
            };

            picker = {
                ...picker,
                DecidedToAdmitDate: localAdmit || null,
                DecidedToAdmitTime: localAdmit || null,
                DecidedToAdmitDateAndTime: localAdmit || null,
            };
        }

        if (props.patient?.facility) {
            checkLastObjectForDecidedToAdmit(props.patient?.diagnosis?.primary, 'procedureTime', 'procedureDate');
            checkLastObjectForDecidedToAdmit(props.patient?.studies?.referrals?.ReferralsToOtherServices, 'ActivityServiceRequestTime_EmergencyCare', 'ActivityServiceRequestDate_EmergencyCare');
        }

        // KPI Clinically Ready to Proceed
        // Clinical Conclusion – Ready to Admit
        // Clinical Conclusion – Ready to Transfer
        // Clinical Conclusion – Ready to Discharge
        // Discharge Time
        if (props.patient?.metadata?.firstClinicallyReadyToProceedSaved?.when) {
            const localRdyToProc = moment.utc(props.patient?.metadata?.firstClinicallyReadyToProceedSaved?.when).local();

            dataToBlock = {
                ...dataToBlock,
                EmergencyCareClinicallyReadyToProceedTimeStamp: moment(props.patient?.metadata?.firstClinicallyReadyToProceedSaved?.when),
            };

            picker = {
                ...picker,
                EmergencyCareClinicallyReadyToProceedTimeStamp: localRdyToProc,
            };
        } else if (props.patient.clinicalConclusion) {
            const localRdyToProc = moment.utc(props.patient.clinicalConclusion?.when).local();

            dataToBlock = {
                ...dataToBlock,
                EmergencyCareClinicallyReadyToProceedTimeStamp: props.patient.clinicalConclusion?.when
                    ? (moment(props.patient.clinicalConclusion?.when, config.defaultDateTimeFormat).isValid() ? moment(props.patient.clinicalConclusion?.when, config.defaultDateTimeFormat) : moment(props.patient.clinicalConclusion?.when))
                    : null,
            };

            picker = {
                ...picker,
                EmergencyCareClinicallyReadyToProceedTimeStamp: localRdyToProc || null,
            };
        }

        if (props.patient.admission?.endDate) {
            dataToBlock = {
                ...dataToBlock,
                EmergencyCareDepartureDate: moment(props.patient.admission?.endDate),
                EmergencyCareDepartureTime: moment(props.patient.admission?.endDate),
                UrgentAndEmergencyCareActivityEndTimestamp: moment(props.patient.admission?.endDate),
            };
        }
        data = {
            ...data,
            ...dataToBlock,
        };

        setDischarge(prevState => ({...prevState, ...data}));
        setPickers({...picker});
        // disableData.current = {...cleanObject(dataToBlock)};
    }, []);

    // todo update
    const updateData = () => {
        const admitUTC = UtilsMomentJS.getUTCDateFromECDSFormat(pickers.DecidedToAdmitDate, pickers.DecidedToAdmitTime);
        const departureUTC = UtilsMomentJS.getUTCDateFromECDSFormat(pickers.EmergencyCareDepartureDate, pickers.EmergencyCareDepartureTime);
        const readyToProceedUTC = pickers.EmergencyCareClinicallyReadyToProceedTimeStamp
            ? moment(pickers.EmergencyCareClinicallyReadyToProceedTimeStamp).utc().set({second: 0}).format(config.ecdsDateTimeFormat)
            : null;

        const data = {
            DecidedToAdmitDate: admitUTC ? admitUTC.format(config.ecdsDateFormat) : '',
            DecidedToAdmitTime: admitUTC ? admitUTC.format(config.ecdsTimeFormat) : '',
            DecidedToAdmitDateAndTime: '',
            ActivityTreatmentFunctionCode_DecisionToAdmit: discharge.ActivityTreatmentFunctionCode_DecisionToAdmit
                ? discharge.ActivityTreatmentFunctionCode_DecisionToAdmit.__code : '',
            EmergencyCareClinicallyReadyToProceedTimeStamp: readyToProceedUTC || '',
            EmergencyCareDischargeStatus_SnomedCt: discharge.EmergencyCareDischargeStatus_SnomedCt
                ? discharge.EmergencyCareDischargeStatus_SnomedCt.snomedCode : '',
            EmergencyCareDepartureDate: departureUTC ? departureUTC.format(config.ecdsDateFormat) : '',
            EmergencyCareDepartureTime: departureUTC ? departureUTC.format(config.ecdsTimeFormat) : '',
            UrgentAndEmergencyCareActivityEndTimestamp: '',
            SafeguardingConcern_SnomedCt: getSnomedCode(discharge.SafeguardingConcern_SnomedCt),
            EmergencyCareDischargeDestination_SnomedCt: discharge.EmergencyCareDischargeDestination_SnomedCt
                ? discharge.EmergencyCareDischargeDestination_SnomedCt.snomedCode : '',
            OrganisationSiteIdentifier_DischargeFromEmergencyCare: discharge.OrganisationSiteIdentifier_DischargeFromEmergencyCare,
            EmergencyCareDischargeFollowUp_SnomedCt: discharge.EmergencyCareDischargeFollowUp_SnomedCt
                ? discharge.EmergencyCareDischargeFollowUp_SnomedCt.snomedCode : '',
            EmergencyCareDischargeInformationGiven_SnomedCt: discharge.EmergencyCareDischargeInformationGiven_SnomedCt
                ? discharge.EmergencyCareDischargeInformationGiven_SnomedCt.snomedCode : '',
            UrgentAndEmergencyCareDischargeInformationGivenIndicator: '',
        };
        props.setData(data);
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (validate()) {
                updateData();
            }
        }, 400);
        return () => clearTimeout(timeoutId);
    }, [discharge, pickers]);

    const changeHandler = (event) => {
        setDischarge((prevState) => ({...prevState, [event.target.name]: event.target.value}));
    };

    const setDataDictionary = (data) => {
        changeHandler({
            target: {
                name: data[0],
                value: data?.[1] || null,
            },
        });
    };

    function multiDataHandler(_e, val) {
        changeHandler({
            target: {
                name: 'SafeguardingConcern_SnomedCt',
                value: val,
            },
        });
    }

    const getSnomedCode = (list) => {
        let data = [];
        list.map(entry => data.push(entry.snomedCode));
        return data;
    };

    const validate = () => {
        let check = true;

        if (validationEnabled) {
            let errorData = {
                activity: discharge.ActivityTreatmentFunctionCode_DecisionToAdmit ? !props.checkLenght(3, 3, discharge.ActivityTreatmentFunctionCode_DecisionToAdmit.__code) : false,
                dischargeStatus: discharge.EmergencyCareDischargeStatus_SnomedCt ? !props.checkLenght(6, 18, discharge.EmergencyCareDischargeStatus_SnomedCt) : false,
                dischargeDestination: discharge.EmergencyCareDischargeDestination_SnomedCt ? !props.checkLenght(6, 18, discharge.EmergencyCareDischargeDestination_SnomedCt) : false,
                orgId: discharge.OrganisationSiteIdentifier_DischargeFromEmergencyCare ? !props.checkLenght(5, 9, discharge.OrganisationSiteIdentifier_DischargeFromEmergencyCare) : false,
                dischargeFollowUp: discharge.EmergencyCareDischargeFollowUp_SnomedCt ? !props.checkLenght(6, 18, discharge.EmergencyCareDischargeFollowUp_SnomedCt) : false,
                // dischargeInfoGiven: discharge.EmergencyCareDischargeInformationGiven_SnomedCt ? !props.checkLenght(6, 18, discharge.EmergencyCareDischargeInformationGiven_SnomedCt) : false,
            };

            if (discharge.SafeguardingConcern_SnomedCt && discharge.SafeguardingConcern_SnomedCt.length > 0) {
                let check = false;
                for (const element of discharge.SafeguardingConcern_SnomedCt) {
                    if (check) {
                        break;
                    }
                    check = !props.checkLenght(6, 18, element);
                    errorData = {
                        ...errorData,
                        safeguarding: check,
                    };
                }
            }
            setError(prevState => ({...prevState, ...errorData}));

            for (let prop in errorData) {
                if (errorData[prop]) {
                    check = false;
                    break;
                }
            }

            if (props.valid) {
                const validObj = {
                    name: 'Discharge from Emergency Care',
                    groupStatus: 'R',
                    mandatory: false,
                    errors: !check,
                    pickers: !!errorPicker.current.admitDateAndTime || !!errorPicker.current.activityEndTimestamp || !!errorPicker.current.proceedDate,
                };
                props.valid(prevState => ({...prevState, 'discharge': validObj}));
            }
        }
        return check;
    };

    const dateHandler = (name, event) => {
        const date = moment(event).set('second', 0);
        setPickers((prevState) => ({...prevState, [name]: date}));
    };

    if (props.accordion !== 'Discharge from Emergency Care') return null;

    return (
        <div className={classes.root}>
            <Grid container spacing={2} direction="row" style={{marginTop: 1}}>
                <Grid item xs={12}>
                    <AgyleDatePicker
                        name="DecidedToAdmitDateAndTime"
                        label={t('Decided to Admit Date and Time')}
                        type="dateTime"
                        value={pickers.DecidedToAdmitDateAndTime || null}
                        onChange={(e) => dateHandler('DecidedToAdmitDateAndTime', e)}
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        onError={event => errorPicker.current = {
                            ...errorPicker.current,
                            admitDateAndTime: event,
                        }}
                        readOnly={!!disableData?.current.DecidedToAdmitDateAndTime}
                        error={error.admitDateAndTime}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <ActivityTreatmentECDS
                        name="ActivityTreatmentFunctionCode_DecisionToAdmit"
                        label={t('Activity Treatment')}
                        value={discharge.ActivityTreatmentFunctionCode_DecisionToAdmit}
                        onChange={(_e, val) => setDataDictionary(['ActivityTreatmentFunctionCode_DecisionToAdmit', val])}
                        error={error.activity}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <AgyleTextField
                        name="OrganisationSiteIdentifier_DischargeFromEmergencyCare"
                        label={t('Org id - Discharge from Emergency Care')}
                        onChange={changeHandler}
                        value={discharge.OrganisationSiteIdentifier_DischargeFromEmergencyCare || ''}
                        error={error.orgId}
                        helperText={error.orgId ? 'Input must be between 5 and 9 characters long.' : ''}
                        maxLength={9}
                        minLength={5}
                        readOnly={!!disableData?.current.OrganisationSiteIdentifier_DischargeFromEmergencyCare}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleDatePicker
                        name="EmergencyCareDepartureDate"
                        label={t('Departure Date and Time')}
                        type="dateTime"
                        value={pickers.UrgentAndEmergencyCareActivityEndTimestamp || null}
                        onChange={(e) => dateHandler('UrgentAndEmergencyCareActivityEndTimestamp', e)}
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        onError={event => errorPicker.current = {
                            ...errorPicker.current,
                            activityEndTimestamp: event,
                        }}
                        readOnly={!!disableData?.current.UrgentAndEmergencyCareActivityEndTimestamp}
                        error={error.activityEndTimestamp}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <DischargeStatus
                        name="EmergencyCareDischargeStatus_SnomedCt"
                        label={t('Discharge Status')}
                        value={discharge.EmergencyCareDischargeStatus_SnomedCt}
                        error={error.dischargeStatus}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareDischargeStatus_SnomedCt', val])}
                        disabled={!!disableData?.current.EmergencyCareDischargeStatus_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <SafeguardingECDS
                        name="SafeguardingConcern_SnomedCt"
                        error={error.safeguarding}
                        label={t('Safeguarding Concern')}
                        value={discharge.SafeguardingConcern_SnomedCt}
                        onChange={multiDataHandler}
                        disabled={!!disableData?.current.SafeguardingConcern_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        ecdsVersion={version}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <DischargeDestination
                        name="EmergencyCareDischargeDestination_SnomedCt"
                        label={t('Discharge Destination')}
                        value={discharge.EmergencyCareDischargeDestination_SnomedCt}
                        error={error.dischargeDestination}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareDischargeDestination_SnomedCt', val])}
                        disabled={!!disableData?.current.EmergencyCareDischargeDestination_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <DischargeFollowUp
                        name="EmergencyCareDischargeFollowUp_SnomedCt"
                        label={t('Discharge Follow Up')}
                        value={discharge.EmergencyCareDischargeFollowUp_SnomedCt}
                        error={error.dischargeFollowUp}
                        onChange={(_e, val) => setDataDictionary(['EmergencyCareDischargeFollowUp_SnomedCt', val])}
                        disabled={!!disableData?.current.EmergencyCareDischargeFollowUp_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                {version === 'V3' &&
                    <Grid item xs={12}>
                        <DischargeInfoGiven
                            name="EmergencyCareDischargeInformationGiven_SnomedCt"
                            label={t('Discharge Information Given')}
                            value={discharge.EmergencyCareDischargeInformationGiven_SnomedCt}
                            error={error.dischargeInfoGiven}
                            onChange={(_e, val) => setDataDictionary(['EmergencyCareDischargeInformationGiven_SnomedCt', val])}
                            disabled={!!disableData?.current.EmergencyCareDischargeInformationGiven_SnomedCt}
                            admitDate={props.patient?.admission?.startDate}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>
                }

                {version === 'V4' &&
                    <>
                        <Grid item xs={12}>
                            <FormLabel>
                                {t('Urgent And Emergency Care Discharge Information Given Indicator')}
                            </FormLabel>
                        </Grid>

                        <Grid item xs={12} style={{display: 'flex', flexDirection: 'column', gap: 5}}>
                            <AgyleCheckBox
                                label={t('Yes - a copy of the discharge letter was given to the PATIENT')}
                                value={discharge.UrgentAndEmergencyCareDischargeInformationGivenIndicator === 'Y'}
                                onChange={() => setDischarge(prevState => ({
                                    ...prevState,
                                    UrgentAndEmergencyCareDischargeInformationGivenIndicator: 'Y',
                                }))}
                            />

                            <AgyleCheckBox
                                label={t('No - a copy of the discharge letter was NOT given to the PATIENT')}
                                value={discharge.UrgentAndEmergencyCareDischargeInformationGivenIndicator === 'N'}
                                onChange={() => setDischarge(prevState => ({
                                    ...prevState,
                                    UrgentAndEmergencyCareDischargeInformationGivenIndicator: 'N',
                                }))}
                            />
                        </Grid>
                    </>
                }

                <Grid item xs={12}>
                    <AgyleDatePicker
                        name="EmergencyCareClinicallyReadyToProceedTimeStamp"
                        label={t('Clinically Ready to Proceed Date and Time')}
                        type="dateTime"
                        value={pickers.EmergencyCareClinicallyReadyToProceedTimeStamp || null}
                        onChange={(e) => dateHandler('EmergencyCareClinicallyReadyToProceedTimeStamp', e)}
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        onError={event => errorPicker.current = {
                            ...errorPicker.current,
                            proceedDate: event,
                        }}
                        readOnly={!!disableData?.current.EmergencyCareClinicallyReadyToProceedTimeStamp}
                        error={error.timestamp}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default DischargeECDS;